/***************
 * NetX extensions to Underscore's _ object, adding miscellaneous utility functions.
 * NOTE: Be sure to document new items in https://confluence.netx.net/display/DEV/X7+Library+Overrides%2C+Extensions+and+Mixins
 */
import Modernizr from 'core-libs/modernizr/modernizr';
import _ from 'underscore';
import {arrayToCsv, csvToArray, toCamelCase} from '@netx/core-utils';

/**
 * Convert arguments to array
 * @param {arguments} args Arguments
 * @return {array}
 */
export const argsToArray = (args) => {
	// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/arguments
	return args.length === 1 ? [args[0]] : Array.apply(null, args);
};

/**
 * Convert integer byte counts into human-readable strings
 * @param {number}  bytes     Bytes to be formatted
 * @param {boolean} floor     Whether to Math.floor the return
 * @param {string}  maxScale The maximum unit
 * @returns {string} converted bytes affixed with units
 */
export const bytes = (bytes, floor, maxScale) => {
	bytes = parseFloat((bytes || 0).toString());
	var i = -1;
	var byteUnits = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	var idx = _.indexOf(byteUnits, maxScale);
	if (idx > -1) {
		i = idx;
		bytes = bytes ? bytes / Math.pow(1000, idx + 1) : 0;
	} else {
		do {
			bytes = bytes / 1000;
			i++;
		} while (bytes >= 1000);
	}

	if (bytes > 0) {
		if (floor) {
			return Math.floor(bytes) + ' ' + byteUnits[i];
		} else {
			// We need more info when it comes to over a gig
			if (i > 1) {
				return Math.max(bytes, 0.01).toFixed(2) + ' ' + byteUnits[i];
			} else {
				return Math.max(bytes, 0.1).toFixed(1) + ' ' + byteUnits[i];
			}
		}
	} else {
		return idx > -1 ? '0 ' + byteUnits[idx] : '0 kB';
	}
};

/**
 * Returns str without quotes and spaces commas
 * @param {string} str String to clean up
 * @returns {string} Clean spaced string
 */
export const cleanQuotes = (str) => {
	return str.replace(/"/g, '').replace(/,/g, ', ');
};

/**
 * @param {Date|number|string} d Date string
 * @returns {string} HTML date string (YYYY-MM-DD)
 */
export const dateToHTML = (d) => {
	if (!_.isUndefined(d) && d) {
		// Support milliseconds
		if (_.isNumber(d)) {
			d = new Date(d);
		}

		if (d instanceof Date) {
			return [
				d.getFullYear(),
				pad(d.getMonth() + 1, 2),
				pad(d.getDate(), 2),
			].join('-');
		}

		var temp = d.split('/');
		if (temp.length && temp[0].length === 4)
			return d.replace(/(\d+)\/(\d+)\/(\d+)/, '$1-$2-$3');

		return d.replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2');
	}
	return '';
};

/**
 * Escape asset attribute name
 * (Do what underscore's _.escape does, then encode any open & close parens as HTML entities.)
 *
 * @param {string} string Attribute name
 * @return {string} Escaped attribute name
 */
export const escapeAttributeName = (string) => {
	string = _.escape(string);
	string = string
		.replace(/\(/g, '&#40;')
		.replace(/\)/g, '&#41;')
		.replace(/\[/g, '&#91;')
		.replace(/\]/g, '&#93;');
	return string;
};

/**
 * @param {object} exports module.exports
 * @param {array} actions Actions
 * @return {array} Extended actions
 */
export const extendActions = (exports, actions) => {
	var _action;
	_.each(actions, function (action) {
		_action = _.findWhere(exports, {event: action.event});
		_action && _.extend(_action, action);
	});
	return exports;
};

/**
 * Get the body of a function as string
 * https://stackoverflow.com/questions/3179861/javascript-get-function-body
 * @method
 * @param {function} func Function
 * @return {string} Function body
 */
export const getFunctionBody = (func) => {
	var fstring = func.toString();
	return fstring.substring(fstring.indexOf('{') + 1, fstring.lastIndexOf('}'));
};

/**
 * Returns a worker
 * @method
 * @param {function} func Worker function
 * @return {Worker} Worker
 */
export const getInlineWorker = (func) => {
	var blob = new Blob([getFunctionBody(func)]);

	var worker = new Worker(window.URL.createObjectURL(blob));
	return worker;
};

/**
 * @param {mixed} value Value
 * @return {boolean}
 */
export const isEmptyValue = (value) => {
	if (Array.isArray(value)) {
		return value.length === 0 || (value.length === 1 && value[0] === '');
	}
	return value == null || (value + '').trim() === '';
};

/**
 * Is this a touch device and smaller than mobile-breakpoint-max?
 * @function
 * @param {boolean} testTouch Inlcude testing for touch
 */
export const isMobile = (testTocuh = true) => {
	return (!testTocuh || Modernizr.touch) && window.innerWidth < 767;
};

/**
 * Returns str (perhaps representing an integer)
 * left-padded with zeroes to at least 'max' characters in width
 * example: pad("3",4) == "0003"
 * @param {string} str String to pad
 * @param {integer} max The amount of '0's to pad to
 * @returns {string} Padded string
 */
export const pad = (str, max) => {
	str = str + '';
	while (str.length < max) {
		str = '0' + str;
	}
	return str;
};

/**
 * Provided with full dimensions and dimensions to fit, will return resized dimensions
 * @param {object} dimensions Full dimensions of thing to fit
 * @param {object} fitDimensions Dimensions to fit to
 * @return {object} Resized dimensions
 */
export const resizeToFit = (dimensions, fitDimensions) => {
	let height = dimensions.height;
	let width = dimensions.width;
	let newDimensions = _.clone(dimensions);

	const aspectRatio = dimensions.aspectRatio || width / height;
	const maxWidth = fitDimensions.width;
	const maxHeight = fitDimensions.height;

	if (maxHeight && maxWidth) {
		if (maxWidth < width) {
			width = maxWidth;
			height = Math.round(width / aspectRatio);
		}
		if (maxHeight < height) {
			height = maxHeight;
			width = Math.round(height * aspectRatio);
		}
		// Make a second pass to be sure our resize did not cause the height or width to exceed the fitDimensions
		if (maxHeight < height) {
			height = maxHeight;
			width = Math.round(height * aspectRatio);
		}
		if (maxWidth < width) {
			width = maxWidth;
			height = Math.round(width / aspectRatio);
		}
		newDimensions = {
			height: height,
			width: width,
		};
	}
	return newDimensions;
};

/**
 * Unescape asset attribute name in form fields.
 * (Specifically, do everything underscore's _.unescape method does,
 * and also decode the HTML entities for open & close parens, if present.)
 *
 * @param {string} string Attribute name
 * @return {string} Unescaped attribute name
 */
export const unescapeAttributeName = (string) => {
	string = _.unescape(string);
	string = string
		.replace(/&#40;/g, '(')
		.replace(/&#41;/g, ')')
		.replace(/&#91;/g, '[')
		.replace(/&#93;/g, ']');
	return string;
};

_.mixin({
	bytes,

	/**
	 * @param {array|number} items Either an array of items or a number
	 * @param {number} currentIndex Current index in items
	 * @param {number} itemsToDisplay Number of items to display
	 * @param {mixed} ellipsis What to use for ellipis
	 * @returns {array} array of crumbs
	 */
	crumbs: function (items, currentIndex, itemsToDisplay, ellipsis) {
		currentIndex = parseInt(currentIndex) || 1;
		itemsToDisplay = parseInt(itemsToDisplay) || 2;
		ellipsis = ellipsis || '...';

		var crumbArray = [],
			totalItems = _.isArray(items) ? items.length : parseInt(items) || 0,
			firstItem = 1;

		if (totalItems) {
			// Subtract 2 ( for 1 on each side ) then divide by 2
			var adjacentCount = Math.floor((itemsToDisplay - 2) / 2),
				firstAdjacentItem,
				lastAdjacentItem;

			// Page count must be greater than pages to display
			if (totalItems >= itemsToDisplay) {
				if (currentIndex <= adjacentCount + adjacentCount) {
					firstAdjacentItem = firstItem;
					lastAdjacentItem = Math.min(
						firstItem + adjacentCount + adjacentCount,
						totalItems,
					);
				} else if (currentIndex > totalItems - adjacentCount - adjacentCount) {
					lastAdjacentItem = totalItems;
					firstAdjacentItem = totalItems - adjacentCount - adjacentCount;
				} else {
					firstAdjacentItem = currentIndex - adjacentCount;
					lastAdjacentItem = currentIndex + adjacentCount;
				}

				if (firstAdjacentItem > firstItem) {
					crumbArray.push(_.isArray(items) ? items[firstItem - 1] : firstItem);
					if (firstAdjacentItem > firstItem + 1) {
						crumbArray.push(ellipsis);
					}
				}
				for (var i = firstAdjacentItem; i <= lastAdjacentItem; i++) {
					crumbArray.push(_.isArray(items) ? items[i - 1] : i);
				}
				if (lastAdjacentItem < totalItems) {
					if (lastAdjacentItem < totalItems - 1) {
						crumbArray.push(ellipsis);
					}
					crumbArray.push(
						_.isArray(items) ? items[totalItems - 1] : totalItems,
					);
				}
			}
			// Just spit em all out
			else {
				_.times(totalItems, function (n) {
					crumbArray.push(_.isArray(items) ? items[n] : n + 1);
				});
			}
		}
		return crumbArray;
	},

	/**
	 * isURL: returns true if a string is a valid URL.
	 * @param {string} str the string to be tested
	 * @return {boolean} Is URL
	 */
	isURL: function (str) {
		return !!_.matchURL(str);
	},

	/**
	 * This is harder than it seems.
	 * The following page shows a fairly complete test suite for regexp URL validation
	 * using several popular internet options:
	 *
	 * 	https://mathiasbynens.be/demo/url-regex
	 *
	 * isURL uses the only algorithm on this page that passed all tests --
	 * 	https://gist.github.com/dperini/729294 -- by Diego Perini
	 *
	 * @param {string} str the string to be tested
	 * @return {array|null} the result of String.match()
	 */
	matchURL: function (str) {
		return str.match(
			new RegExp(
				'^' +
					// protocol identifier
					'(?:(?:https?|ftp)://)' +
					// user:pass authentication
					'(?:\\S+(?::\\S*)?@)?' +
					'(?:' +
					// IP address exclusion
					// private & local networks
					'(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
					'(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
					'(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
					// IP address dotted notation octets
					// excludes loopback network 0.0.0.0
					// excludes reserved space >= 224.0.0.0
					// excludes network & broacast addresses
					// (first & last IP address of each class)
					'(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
					'(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
					'(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
					'|' +
					// host name
					'(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)' +
					// domain name
					'(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*' +
					// TLD identifier
					'(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))' +
					// TLD may end with dot
					'\\.?' +
					')' +
					// port number
					'(?::\\d{2,5})?' +
					// resource path
					'(?:[/?#]\\S*)?' +
					'$',
				'i',
			),
		);
	},

	/**
	 * Determine if a string (or number) is numeric; different from _.isNumber, which just tests type
	 * http://stackoverflow.com/questions/18082/validate-numbers-in-javascript-isnumeric
	 * @param {*} String/Integer
	 * @returns {bool} Whether or not it is a number to test
	 */
	isNumeric: function (n) {
		return !isNaN(parseFloat(n)) && isFinite(n);
	},

	/**
	 * @method
	 * @param {string} str String to parse
	 * @return {string} Parsed URL string
	 */
	parseURLS: function (str) {
		return (
			str &&
			str.replace(
				/(https?:\/\/.*?[^\s|'|"]+)/g,
				'<a title="$1" class="url-link" target="_blank" href="$1">$1</a>',
			)
		);
	},

	/**
	 * Convert a string "true"/"false" to boolean
	 * @param {string} val String to convert
	 * @returns {bool} The string as a boolean
	 */
	toBoolean: function (val) {
		if (val.toLowerCase() == 'false') return false;
		return true;
	},

	/**
	 * For historical reasons, the DAM sometimes encodes object data as an array of LabelValueBeans.
	 * objectToLvbs and lvbsToObject are utilities to convert in and out of that format.
	 * @param {object} obj Label value bean object to convert to array
	 * @returns {array} Label value been object as array
	 */
	objectToLvbs: function (obj) {
		return _.map(obj, function (v, l) {
			return {label: l, value: v};
		});
	},

	/**
	 * For historical reasons, the DAM sometimes encodes object data as an array of LabelValueBeans.
	 * objectToLvbs and lvbsToObject are utilities to convert in and out of that format.
	 * @param {array} lvb Label value bean array to convert to object
	 * @returns {array} Label value been array as object
	 */
	lvbsToObject: function (lvb) {
		if (_.isUndefined(lvb) || _.isNull(lvb)) return undefined; // undefined array == undefined object.
		if (!_.isArray(lvb)) lvb = [lvb]; // convert a single LVB into an array of one.
		if (lvb.length == 0) return {}; // empty array == empty object.

		var memo = {};
		_.reduce(
			lvb,
			function (memo, b) {
				memo[b.label] = b.value;
				return memo;
			},
			memo,
		);
		return memo;
	},

	/**
	 * An attempt at a utility function to turn a bean label-value array like [ {label: 'foo', value: 123 }, ... ] into
	 * a more useful object like { foo: 123, ... }
	 * NOTE: it expands into sub-objects when label has dots.  or should.  (see _dotExpand() above)
	 * NOTE: this is destructive to the passed in array lv, as it would otherwise get set on the model
	 * TODO: should this be done instead outside of this function? probably maybe.
	 * @param {array} lv Bean label-value array
	 * @returns {object} Bean label-value array as object
	 */
	lvbsToDeepObject: function (lv) {
		var obj = {};
		for (var i = 0; i < lv.length; i++) {
			var val = lv[i].value;
			//being outrageously bold here and assuming no string in user prefs wants to be literal "true" or "false"
			// if this turns out not to be the case, my apologies and this will have to do a case-by-case check on what to turn into boolean.  :(  -jon
			if (val == 'true' || val == 'false') val = _.toBoolean(val);
			_._dotExpand(obj, lv[i].label.split('.'), val);
		}
		lv.length = 0; //the destructive part
		return obj;
	},

	/**
	 * Really just a utility function for beanArrayToObject.
	 * This will take (obj, [foo, bar, baz, fu], val) and alter obj to be: obj[foo][bar][baz][fu] = val (creating sub-objects iff necessary)
	 * NOTE: this will alter/affect the passed-in obj, and the return value is only useful for recursing here
	 * @param {object} obj Object to expand from array
	 * @param {array} levels An array of levels which to expand the object to
	 * @param {*} value The value to assign to this new object level
	 */
	_dotExpand: function (obj, levels, value) {
		var key = levels.shift();

		//trivial case where initial levels = [] ... just to do the right thing  (i think?)
		if (_.isUndefined(key)) {
			obj = value;
			return undefined;
		}

		if (levels.length === 0) {
			obj[key] = value;
			return obj;
		}

		//still more levels to go, so lets make sure this has a home
		if (!obj[key]) obj[key] = {};
		//if (typeof obj[key] != 'object') return; //????????  TODO

		return _._dotExpand(obj[key], levels, value);
	},

	/**
	 * Complement to _dotExpand(). Will return an array of [{label: value}, {label: value},...] of leaf nodes
	 * e.g. {a: {b: 1, c: 2}} -> [ {a.b: 1}, {a.c: 2} ]
	 * @param {object} obj Object to collapse to an array
	 * @returns {array} Collapsed object as an array
	 */
	_dotCollapse: function (obj) {
		var arr = [];
		var o = {};
		for (var key in obj) {
			if (typeof obj[key] == 'object') {
				var r = _._dotCollapse(obj[key]);
				for (var i = 0; i < r.length; i++) {
					for (var rk in r[i]) {
						//should really only be one key but how to do this shorthand?
						o = {};
						o[key + '.' + rk] = r[i][rk];
						arr.push(o);
					}
				}
			} else {
				o = {};
				/////////////TODO deal with conversion to boolean  !?!
				o[key] = obj[key];
				arr.push(o);
			}
		}
		return arr;
	},

	dateToHTML,

	/**
	 * The DAM (sometimes) stores dates on asset attributes in dd/mm/yyyy format, but HTML forms use yyyy-mm-dd format.
	 * @param {string} d Date string
	 * @returns {string} DAM date string
	 */
	dateToDAM: function (d) {
		if (!_.isUndefined(d)) {
			var temp = d.split('-');
			if (temp.length && temp[0].length === 4)
				return d.replace(/(\d+)-(\d+)-(\d+)/, '$1/$2/$3');

			return d.replace(/(\d+)-(\d+)-(\d+)/, '$3/$1/$2');
		}
	},
	/**
	 * Provided with a date or time (in milliseconds), get a new date in the users timezone
	 * If nothing is provided you will get now
	 * @param {date|number} d Date or time
	 * @return {date} Local date
	 */
	dateToLocalTimezone: function (d) {
		if (!(d instanceof Date)) {
			if (_.isNumber(d)) {
				d = new Date(d);
			} else {
				return new Date();
			}
		}

		var time = d.getTime(),
			timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000;

		return new Date(time + timezoneOffset);
	},
	/**
	 * The DAM stores timestamps on assets (and some other objects) in a completely different format:
	 * yyyy-mm-dd hh:mm:ss.s
	 * (.s == tenths of a second ... sometimes there and sometimes not)
	 * This function takes one of those strings, and returns a JS Date object.
	 * @param {string} d asset timestamp
	 * @returns {Date} JS Date object
	 */
	dateFromAsset: function (d) {
		var t = d.match(/(\d+)-(\d+)-(\d+) (\d+):(\d+):(\d+)/);
		if (!t || !t.length) {
			throw new Error("dateFromAsset: bad date '" + d + "'");
		}

		return new Date(t[1], t[2] - 1, t[3], t[4], t[5], t[6]);
	},

	/**
	 * The opposite: turn a js Date object into a DAM asset timestamp
	 * @param {Date} d A Date object.  If omitted, we create one with the current time.
	 * @param {boolean} omitTime Omit the time piece
	 * @returns {string} asset timestamp in local timezone
	 */
	dateToAsset: function (d, omitTime) {
		d = d || new Date();
		var ret =
			d.getFullYear() +
			'-' +
			pad(d.getMonth() + 1, 2) +
			'-' +
			pad(d.getDate(), 2);
		if (!omitTime) {
			ret +=
				' ' +
				pad(d.getHours(), 2) +
				':' +
				pad(d.getMinutes(), 2) +
				':' +
				pad(d.getSeconds(), 2) +
				'.0';
		}
		return ret;
	},

	/**
	 * Same thing, but in UTC.
	 * @param {Date} d A Date object.  If omitted, we create one with the current time.
	 * @returns {string} asset timestamp in UTC
	 */
	dateToAssetUTC: function (d) {
		return [
			d.getUTCFullYear(),
			'-',
			pad(d.getUTCMonth(), 2),
			'-',
			pad(d.getUTCDate(), 2),
			' ',
			pad(d.getUTCHours(), 2),
			':',
			pad(d.getUTCMinutes(), 2),
			':',
			pad(d.getUTCSeconds(), 2),
			'.0',
		].join('');
	},

	/**
	 * Convert string date 'yyyy-mm-dd' to JavaScript Date object
	 * @param {string} dateString Date string formatted 'yyyy-mm-dd'
	 * @returns {date} JavaScript Date object
	 */
	dateFromString: function (d) {
		// Try with "-"
		var t = d.match(/(\d+)-(\d+)-(\d+)/);
		if (!t || !t.length) {
			// Try with "/"
			t = d.match(/(\d+)\/(\d+)\/(\d+)/);
			if (!t || !t.length) {
				throw new Error("dateFromString: bad date '" + d + "'");
			}
		}

		var nd = new Date(t[1], t[2] - 1, t[3]);
		return nd;
	},
	/**
	 * Convert month integer to month name string
	 * @param {integer} m Month from 0 - 11
	 * @returns {string} month name
	 */
	getMonthString: function (m) {
		if (!_.isUndefined(m) && m < 12 && m > -1) {
			var months = [
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
			];
			return months[m];
		}
		return '';
	},
	/**
	 * Convert day integer to day name string
	 * @param {integer} d Day from 0 - 6
	 * @returns {string} day name
	 */
	getDayString: function (d) {
		if (!_.isUndefined(d) && d < 7 && d > -1) {
			var days = [
				'Sunday',
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday',
			];
			return days[d];
		}
		return '';
	},
	/**
	 * Convert integer to a stylized string (1000000 -> "1,000,000")
	 * @param {integer} number Number you want formatted
	 * @returns {string} formatted number string
	 */
	formatNumber: function (number) {
		return number.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
	},
	/**
	 * The jQuery Promise implementation uses some crazy internal class-hackery
	 * such that 'instanceof' is useless for detecting either $.Promise or $.Deferred classes.
	 * This is our go-to pattern for detecting either thing:
	 * Is it an object with a then() function?
	 * (Please update as the pattern improves, or we use more/different Promise libraries.)
	 *
	 * @param {object} obj Something that is or is not a promise
	 * @returns {boolean} True if a promise was passed
	 */
	isPromise: function (obj) {
		return obj && _.isFunction(obj.then);
	},

	/**
	 * Takes an array and moves item at index to another index
	 * @param {Array} array Array that in being modified
	 * @param {Int} fromIndex Index of item that is being moved
	 * @param {Int} toIndex Index where item should be placed
	 */
	move: function (array, fromIndex, toIndex) {
		array.splice(toIndex, 0, array.splice(fromIndex, 1)[0]);
		return array;
	},
	/**
	 * Convert milliseconds to a date string yyyy-mm-dd
	 * @param {string|number} ms Milliseconds
	 * @return {string} Date str
	 */
	millisecondsToDateStr: function (ms) {
		ms = parseInt(ms) || 0;
		if (ms) {
			return new Date(ms).toISOString().substring(0, 10);
		}
		return '';
	},
	/**
	 * Convert milliseconds to a date string `November 30, 2016 (13:55:42)`
	 * @param {string|number} ms Milliseconds
	 * @return {string} Date str
	 */
	millisecondsToFullDateStr: function (ms) {
		ms = parseInt(ms) || 0;
		if (ms) {
			var date = new Date(ms);
			return (
				_.getMonthString(date.getMonth()) +
				' ' +
				date.getDate() +
				', ' +
				date.getFullYear() +
				' (' +
				pad(date.getHours(), 2) +
				':' +
				pad(date.getMinutes(), 2) +
				':' +
				pad(date.getSeconds(), 2) +
				')'
			);
		}
		return '';
	},
	/**
	 * Convert milliseconds to a time string hh::mm::ss.ms
	 * @param {string|number} ms Milliseconds
	 * @return {string} Time str
	 */
	millisecondsToTimeStr: function (ms) {
		// Vars
		var floor = Math.floor,
			time = parseInt(ms),
			hour,
			minute,
			second;

		hour = floor(time / 3600000);
		time -= hour * 3600000;
		minute = floor(time / 60000);
		time -= floor(minute * 60000);
		second = floor(time / 1000);
		time -= floor(second * 1000);

		return (
			pad(hour, 2) +
			':' +
			pad(minute, 2) +
			':' +
			pad(second, 2) +
			'.' +
			pad(time, 3)
		);
	},
	/**
	 * Convert string to first letter capitalized. You might recognize me from PHP.
	 * @param {string} str String to have the first letter capitalized
	 * @returns {string} String with first letter capitalized
	 */
	ucfirst: function (str) {
		if (!_.isUndefined(str)) {
			if (str.length > 1) {
				return str.charAt(0).toUpperCase() + str.substring(1);
			} else {
				return str.toUpperCase();
			}
		}
		return '';
	},
	/**
	 * You can't have one without the other ... Lowercase the first character of a string.
	 * @param {string} str String needing decapitalization
	 * @returns {string} String with the first letter lowercased.
	 */
	lcfirst: function (str) {
		if (_.isUndefined(str)) {
			return str;
		}
		if (str.length > 1) {
			return str.slice(0, 1).toLowerCase() + str.slice(1);
		} else {
			return str.toLowerCase();
		}
	},
	/**
	 * Convert string to a proper camelCased string
	 * @param {string} str String you want camelCased
	 * @returns {string} camelCased string
	 */
	toCamelCase,

	/**
	 * Convert a camel cased string and returns an underscored string
	 * @param {string} str camelCased string you want under_scored
	 * @returns {string} under_scored string
	 */
	camelCaseToUnderscore: function (str) {
		if (typeof str !== 'undefined') {
			return str.replace(/([A-Z])/g, function ($1) {
				return '_' + $1.toLowerCase();
			});
		}
		return '';
	},

	/**
	 * Convert a camel cased string and returns an dashed string
	 * @param {string} str camelCased string you want dash-ed
	 * @returns {string} dash-ed string
	 */
	camelCaseToDash: function (str) {
		if (typeof str !== 'undefined') {
			return str.replace(/([A-Z])/g, function ($1) {
				return '-' + $1.toLowerCase();
			});
		}
		return '';
	},

	/**
	 * Convert a camel cased string to a titled string
	 * @param {string} str camelCased string you want To Turn Into A Titled String
	 * @returns {string} titlelized string
	 */
	camelCaseToTitle: function (str) {
		if (typeof str !== 'undefined') {
			var _str = _.ucfirst(
				str.replace(/([A-Z])/g, function ($1) {
					return ' ' + _.ucfirst($1.toLowerCase());
				}),
			);
			if (!_str.includes(' ')) {
				_str = _str.toUpperCase();
			}
			return _str;
		}
		return '';
	},
	/**
	 * Given a collection, an array of models, an array of objects or a single object,
	 * return a Backbone.Collection of the given class.
	 * @param {*} obj Item to be converted to Collection.
	 * @param {Object} targetClass Target Collection type -- a Backbone class extended from Backbone.Collection.
	 * @param {Object} classOptions Any Backbone options to pass to the collection instantiation call.
	 */
	toCollection: function (obj, targetClass, classOptions) {
		if (_.isUndefined(targetClass)) {
			throw new Error('Undefined target class');
		}

		// If we got an undefined object but a defined class,
		// return an empty collection of that class.
		if (_.isUndefined(obj)) {
			return new targetClass([], classOptions);
		}

		// Maybe obj needs no conversion?
		if (obj instanceof targetClass) {
			return obj;
		}

		// if obj is an array of models or bare objects, Backbone will convert it as needed.
		if (_.isArray(obj)) {
			//return new targetClass( obj, classOptions );
			return new targetClass(
				_.map(obj, function (item) {
					// Don't process models.
					if (item.isModel) {
						return item;
					}
					// Let Backbone convert bare objects into models.
					if (_.isObject(item)) {
						return item;
					}
					if (_.isNumeric(item)) {
						// Convert objectIds into objects.
						var model = new targetClass.prototype.model();
						// Fetch them, too.
						model.set(model.idAttribute, item);
						model.fetch();
						return model;
					}
				}),
				classOptions,
			);
		}

		// TODO: this is a troublesome mixin and will no longer work as models are defined at constructor time

		// If obj is a Model of the correct type, it just needs wrapping.
		// If obj is a bare object, Backbone will convert it to the correct type.
		if (obj instanceof targetClass.prototype.model || _.isObject(obj)) {
			return new targetClass([obj], classOptions);
		}

		throw new Error("can't convert " + obj + ' to class ' + targetClass);
	},

	/**
	 * Given a DOM object, a jQuery object, a jQuery selector or a DOM ID
	 * return a jQuery object.
	 * @param {*} obj Item to be converted to jQuery object
	 * @returns jQuery object
	 */
	toJQ: function (obj) {
		if (typeof obj === 'string') {
			/*
			 * Upgrading this to handle a selector is easy, but most of the system still passes bare IDs.
			 * The following bit of logic tries to avoid excess query work:
			 */
			var jqObj = [];

			// this string of yours ... is it a jquery selector?
			if (obj.search(/[#.]/)) {
				// does it have id or class selector sigils?
				jqObj = $(obj);
			}

			// if that failed (or wasn't tried), try backwards-compability: we used to accept a bare element ID
			if (jqObj.length === 0 && obj.indexOf('#') === -1) {
				jqObj = $('#' + obj);
			}
			// if that failed, maybe it's a selector after all.
			if (jqObj.length === 0) {
				jqObj = $(obj);
			}

			obj = jqObj;
		} else if (typeof obj === 'object') {
			if (obj.nodeType) {
				obj = $(obj);
			} else if (typeof obj.jquery === 'string') {
				// already jQuery. leave it alone
				return obj;
			} else {
				console.warn('toJQ received bogus object: ', obj);
			}
		} else {
			console.warn('toJQ received bogus argument: ', obj);
		}

		return obj;
	},

	/**
	 * Convert various things (an assetId, an asset model, or an array of either)
	 * to an array of 1 or more assetIDs.
	 *
	 * 'assets' may be integer assetIDs or full-on asset models,
	 * and it can be either an array of them or just one.
	 * @param {*} assets Array of assetIDs or asset models
	 * @returns {array} A tidy array of assets
	 */
	tidyAssetList: function (assets) {
		if (_.isArguments(assets)) {
			assets = _.rest(assets, 0);
		} else if (!_.isArray(assets)) {
			assets = [assets];
		}

		var tidyAssets = [];

		_.each(assets, function (a, idx, all) {
			if (_.isObject(a)) {
				tidyAssets.push(a.id);
			} else {
				tidyAssets.push(a);
			}
		});

		return tidyAssets;
	},

	/**
	 * Determine where to place a pop-up context menu; takes the click event and a menu element
	 * @param {event} e MouseEvent
	 * @param {object} $menu jQuery object for the context menu
	 * @returns {object} Position
	 */
	getMenuPosition: function (e, $menu) {
		$menu = _.toJQ($menu);

		var mouseX = e.clientX,
			mouseY = e.clientY,
			boundsX = $(window).width(),
			boundsY = $(window).height(),
			menuWidth = $menu.find('.dropdown-menu').outerWidth(),
			menuHeight = $menu.find('.dropdown-menu [role="menu"]').outerHeight(),
			tp = {position: 'absolute'},
			Y,
			X;

		if (mouseX == null) mouseX = e.pageX;
		if (mouseY == null) mouseY = e.pageY;

		// If menu is going to be cutoff at bottom of screen, move it up
		// The +15 is for padding, otherwise menu can open right to the bottom
		// of the screen and look cutoff, even though it isn't
		if (mouseY + menuHeight + 15 > boundsY) {
			Y = {top: mouseY - menuHeight + $(window).scrollTop()};
		} else {
			Y = {top: mouseY + $(window).scrollTop()};
		}

		// If menu is going ot be cutoff at right of screen, move it left
		if (mouseX + menuWidth > boundsX && mouseX - menuWidth > 0) {
			X = {left: mouseX - menuWidth + $(window).scrollLeft()};
		} else {
			X = {left: mouseX + $(window).scrollLeft()};
		}

		// Adjust the position of the menu by its offset parent position.
		var selectorPosition = $menu.offsetParent().offset();
		X.left = X.left - selectorPosition.left;
		Y.top = Y.top - selectorPosition.top;

		return $.extend(tp, Y, X);
	},

	/**
	 * Utils to convert back & forth between the DAM's quoted-csv format for multiselects <-> a normal array.
	 * TODO: escape and unescape double-quotes!
	 * @param {string} csv CSV string
	 * @returns {array} CSV as array
	 */
	csvToArray,

	/**
	 * Utils to convert back & forth between the DAM's quoted-csv format for multiselects <-> a normal array.
	 * TODO: escape and unescape double-quotes and commas!
	 * @param {array} array Array to convert to CSV string
	 * @returns {string} Array as CSV string
	 */
	arrayToCsv,

	// Color utilities
	/**
	 * Convert a color to a new shade
	 * @param {hex} color Hex color '#000000'
	 * @param {integer} percent Percentage of shading as an integer
	 * @returns {hex} New shade of color '#000000'
	 */
	shadeColor: function (color, percent) {
		var R = parseInt(color.substring(1, 3), 16),
			G = parseInt(color.substring(3, 5), 16),
			B = parseInt(color.substring(5, 7), 16);

		R = parseInt((R * (100 + percent)) / 100);
		G = parseInt((G * (100 + percent)) / 100);
		B = parseInt((B * (100 + percent)) / 100);

		R = R < 255 ? R : 255;
		G = G < 255 ? G : 255;
		B = B < 255 ? B : 255;

		var RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16),
			GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16),
			BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

		return '#' + RR + GG + BB;
	},

	/**
	 * Convert Hex color to RGB color
	 * @param {hex} color Hex color to be converted to RGB
	 * @returns {object} RGB color '{ r: 0, g: 0, b: 0 }'
	 */
	hexToRgb: function (hex) {
		var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result
			? {
					r: parseInt(result[1], 16),
					g: parseInt(result[2], 16),
					b: parseInt(result[3], 16),
				}
			: {r: 0, g: 0, b: 0};
	},
	/**
	 * Convert RGB color to HSL color
	 * @param {hex} color Hex color to be converted to RGB
	 * @returns {object} HSL color '{ h: 0, s: 0, l: 0 }'
	 */

	rgbToHsl: function (rgb) {
		var r = rgb[0],
			g = rgb[1],
			b = rgb[2];

		// DEVNOTE: eslint flags this as an error -- https://github.com/eslint/eslint/blob/master/docs/rules/no-sequences.md
		((r /= 255), (g /= 255), (b /= 255));
		// Any reason not to use semicolons instead of commas here?  -m-

		var max = Math.max(r, g, b),
			min = Math.min(r, g, b),
			h,
			s,
			l = (max + min) / 2;

		if (max == min) {
			h = s = 0; // achromatic
		} else {
			var d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
			switch (max) {
				case r:
					h = (g - b) / d + (g < b ? 6 : 0);
					break;
				case g:
					h = (b - r) / d + 2;
					break;
				case b:
					h = (r - g) / d + 4;
					break;
			}
			h /= 6;
		}
		return {h: h, s: s, l: l};
	},

	/**
	 * I feel I must me missing something here.  Is it really the case that
	 * neither JS, jQuery or underscore have a function to parse the URI query string
	 * into an object?  Please prove me wrong. -m-
	 */
	parseQuery: function () {
		return (
			_.chain(
				// get the query string (if any) without the leading ?
				window.location.search
					.slice(1)
					// chop it up by &
					.split('&'),
			)
				// clean out any weird undefined stuff
				.compact()
				// chop each string into a tuple by its equals (if any)
				.map(function (item) {
					if (item) return item.split('=');
				})
				// uri-decode the values, if any
				// (NOTE: not sure if i'm supposed to decode the keys or not.  There is no standard, apparently ...)
				.map(function (array) {
					return array.length == 2 ? [array[0], decodeURI(array[1])] : array;
				})
				// Make that an object.
				.object()
				.value()
		);
	},

	/**
	 * This seems like something underscore would have, but it doesn't:
	 * http://stackoverflow.com/questions/8747561/is-there-any-way-to-rename-js-object-keys-using-underscore-js
	 */
	mapKeys: function (obj, map) {
		var result = {};
		_.chain(obj)
			.keys()
			.each(function (key) {
				var newKey;
				if (_.isFunction(map)) {
					newKey = map(key) || key;
				} else {
					newKey = map[key] || key;
				}
				result[newKey] = obj[key];
			});
		return result;
	},
});

export default _;
